import { Injectable, NgZone, Signal, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, Subject, distinctUntilChanged, map, startWith } from 'rxjs';

export interface SizeObserverSize {
  width: number;
  height: number;
}

@Injectable({
  providedIn: 'root',
})
export class ResizeObserverService {
  readonly #ngZone = inject(NgZone);

  private observer!: ResizeObserver;

  private readonly resizeObserverEntriesSubject$ = new Subject<ResizeObserverEntry[]>();

  constructor() {
    this.#ngZone.runOutsideAngular(() => {
      this.observer = new ResizeObserver((entries) => {
        this.resizeObserverEntriesSubject$.next(entries);
      });
    });
  }

  public observe(target: Element): Signal<SizeObserverSize> {
    return toSignal(this.observe$(target), {
      requireSync: true,
    });
  }

  public observe$(target: Element): Observable<SizeObserverSize> {
    return new Observable<void>((observer) => {
      this.#ngZone.runOutsideAngular(() => {
        this.observer.observe(target);
      });

      const sub = this.resizeObserverEntriesSubject$.subscribe(() => {
        observer.next();
      });

      return () => {
        sub.unsubscribe();
        this.#ngZone.runOutsideAngular(() => {
          this.observer.unobserve(target);
        });
      };
    }).pipe(
      startWith(null),
      map(
        (): SizeObserverSize => ({
          width: target.clientWidth,
          height: target.clientHeight,
        })
      ),
      distinctUntilChanged((prev, curr) => prev.width === curr.width && prev.height === curr.height)
    );
  }
}
